import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "../contexts/AuthContext";
import { useProposal } from "../contexts/ProposalContext";
import { useChat } from "../contexts/ChatContext";
import { useStreamingChat } from "../hooks/useStreamingChat";
import apiClient from "../services/apiClient";

// Components
import Layout from "../components/layout/Layout";
import ProposalCard from "../components/proposals/ProposalCard";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import MessageInput from "../components/chat/MessageInput";
import MessageBubble from "../components/chat/MessageBubble";
import CreateProposalModal from "../components/proposals/CreateProposalModal";
import GeneralChatSidebar from "../components/chat/GeneralChatSidebar";
import PredefinedPromptsDropdown from "../components/chat/PredefinedPromptsDropdown";

const NewDashboard = () => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const { user } = useAuth();
  const {
    proposals,
    loading: proposalsLoading,
    error: proposalsError,
    fetchProposals,
  } = useProposal();
  const {
    sessions,
    messages,
    setMessages, // Add setMessages from ChatContext
    currentSession,
    sendMessage,
    createSession,
    fetchSessions,
    fetchSession,
    loading: chatLoading,
    clearCurrentSession,
    refreshSessions,
    getGeneralSessions, // ADD: Get the function to retrieve general sessions
    // NEW: Chat document methods
    uploadChatDocuments,
    fetchChatDocuments,
    removeChatDocument,
    getChatDocuments,
  } = useChat();

  // Streaming chat hook
  const {
    isStreaming,
    streamingContent,
    sendStreamingMessage,
    completeStreaming,
    clearStreamingContent,
    getFinalStreamedContent, // NEW: Get preserved content
  } = useStreamingChat();

  // State for general chat
  const [isTyping, setIsTyping] = useState(false);
  const [showCreateProposalModal, setShowCreateProposalModal] = useState(false);
  const [currentGeneralSession, setCurrentGeneralSession] = useState(null);
  const [generalChatSidebarOpen, setGeneralChatSidebarOpen] = useState(true);
  // NEW: State for chat documents
  const [currentChatDocuments, setCurrentChatDocuments] = useState([]);
  const [hasUserSelectedSession, setHasUserSelectedSession] = useState(false);

  // Model selection state
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [modelsLoading, setModelsLoading] = useState(true);

  // Stats
  const [stats, setStats] = useState({
    totalProposals: 0,
    activeProposals: 0,
    chatSessions: 0,
    recentActivity: 0,
  });

  // ADDED: Clear any proposal-specific sessions when component mounts
  useEffect(() => {
    console.log(
      "NewDashboard: Component mounted - clearing any proposal sessions"
    );
    // Dispatch event to clear proposal sessions from ChatContext
    window.dispatchEvent(new Event("clearProposalSession"));
  }, []);

  useEffect(() => {
    fetchProposals();
    fetchSessions();
  }, [fetchProposals, fetchSessions]);

  useEffect(() => {
    if (proposals) {
      const active = proposals.filter((p) => p.status === "Active");
      setStats((prev) => ({
        ...prev,
        totalProposals: proposals.length,
        activeProposals: active.length,
      }));
    }
  }, [proposals]);

  useEffect(() => {
    if (sessions) {
      setStats((prev) => ({
        ...prev,
        chatSessions: sessions.length,
      }));
    }
  }, [sessions]);

  // NEW: Effect to load chat documents when session changes
  useEffect(() => {
    if (currentGeneralSession?.session_id) {
      loadChatDocuments(currentGeneralSession.session_id);
    } else {
      setCurrentChatDocuments([]);
    }
  }, [currentGeneralSession?.session_id]);

  // Load available AI models
  useEffect(() => {
    loadAvailableModels();
  }, []);

  const loadAvailableModels = async () => {
    try {
      setModelsLoading(true);
      const response = await apiClient.getAIModels();
      const models = response.data?.models || response.models || [];
      setAvailableModels(models);

      // Set default model
      const defaultModel = models.find((m) => m.isDefault);
      if (defaultModel) {
        setSelectedModel(defaultModel.model);
      }
    } catch (error) {
      console.error("Failed to load available models:", error);
    } finally {
      setModelsLoading(false);
    }
  };

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      try {
        // Method 1: Try scrollIntoView first (smoothest)
        messagesEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "nearest",
        });
      } catch (error) {
        // Method 2: Fallback to manual scroll
        const messagesContainer =
          messagesEndRef.current.closest(".overflow-y-auto");
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      }
    }
  }, []);

  // Enhanced auto-scroll effect with multiple triggers
  useEffect(() => {
    // Immediate scroll for streaming updates
    scrollToBottom();

    // Delayed scroll to handle DOM updates (especially for final message)
    const delayedScrollTimeout = setTimeout(() => {
      scrollToBottom();
    }, 150);

    return () => clearTimeout(delayedScrollTimeout);
  }, [messages, isTyping, streamingContent, scrollToBottom]);

  // Additional effect specifically for when streaming stops
  useEffect(() => {
    if (!isStreaming) {
      // When streaming stops, ensure we scroll to show the complete final message
      setTimeout(() => {
        scrollToBottom();
      }, 200);
    }
  }, [isStreaming, scrollToBottom]);

  // Get general sessions using the function
  const generalSessions = getGeneralSessions();

  // Load most recent general session and its messages, or create one if none exist
  useEffect(() => {
    // Only auto-load if user hasn't manually selected a session
    if (
      !hasUserSelectedSession &&
      generalSessions &&
      generalSessions.length > 0
    ) {
      const mostRecent = generalSessions[0];
      if (
        !currentGeneralSession ||
        currentGeneralSession.session_id !== mostRecent.session_id
      ) {
        setCurrentGeneralSession(mostRecent);
        // Fetch the session to load messages
        fetchSession(mostRecent.session_id);
      }
    } else if (
      !hasUserSelectedSession &&
      generalSessions &&
      generalSessions.length === 0 &&
      !currentGeneralSession &&
      !chatLoading
    ) {
      // If no general sessions exist and we're not loading, auto-create one
      console.log("NewDashboard: No general sessions found, auto-creating one");
      autoCreateGeneralSession();
    }
  }, [
    generalSessions,
    currentGeneralSession,
    fetchSession,
    chatLoading,
    hasUserSelectedSession,
  ]);

  // Separate function for auto-creating session to avoid infinite loops
  const autoCreateGeneralSession = useCallback(async () => {
    try {
      const timestamp = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      const sessionName = `General Chat - ${timestamp}`;

      const newSession = await createSession(sessionName, null);

      if (newSession) {
        const sessionId = newSession.sessionId || newSession.session_id;

        if (sessionId) {
          const normalizedSession = {
            ...newSession,
            session_id: sessionId,
          };

          setCurrentGeneralSession(normalizedSession);
          await fetchSession(sessionId);
        }
      }
    } catch (error) {
      console.error("Failed to auto-create general chat session:", error);
    }
  }, [createSession, fetchSession]);

  // NEW: Load chat documents for a session
  const loadChatDocuments = async (sessionId) => {
    try {
      const docs = await fetchChatDocuments(sessionId);
      setCurrentChatDocuments(docs || []);
    } catch (error) {
      console.error("Failed to load chat documents:", error);
      setCurrentChatDocuments([]);
    }
  };

  // NEW: Upload handler for chat documents
  const handleUploadChatDocument = async (file, sessionId) => {
    try {
      await uploadChatDocuments([file], sessionId);
      // Refresh the documents list
      await loadChatDocuments(sessionId);
    } catch (error) {
      console.error("Failed to upload chat document:", error);
      throw error;
    }
  };

  // NEW: Remove handler for chat documents
  const handleRemoveChatDocument = async (documentId) => {
    if (!currentGeneralSession?.session_id) return;

    try {
      await removeChatDocument(currentGeneralSession.session_id, documentId);
      // Update local state
      setCurrentChatDocuments((prev) =>
        prev.filter((doc) => doc.chat_document_id !== documentId)
      );
    } catch (error) {
      console.error("Failed to remove chat document:", error);
    }
  };

  const handleGeneralChatMessage = async (message, options = {}) => {
    let sessionToUse = currentGeneralSession;

    // If no current session, auto-create one
    if (!sessionToUse) {
      console.log(
        "NewDashboard: No currentGeneralSession, auto-creating one before sending message"
      );

      try {
        const timestamp = new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
        const sessionName = `General Chat - ${timestamp}`;

        const newSession = await createSession(sessionName, null);

        if (newSession) {
          const sessionId = newSession.sessionId || newSession.session_id;

          if (sessionId) {
            const normalizedSession = {
              ...newSession,
              session_id: sessionId,
            };

            setCurrentGeneralSession(normalizedSession);
            sessionToUse = normalizedSession;
          }
        }
      } catch (error) {
        console.error("Failed to auto-create general chat session:", error);
        return;
      }
    }

    if (!sessionToUse) {
      console.error("No valid session available after auto-creation attempt!");
      return;
    }

    const sessionId = sessionToUse.session_id || sessionToUse.sessionId;

    if (!sessionId) {
      console.error("No valid session ID found!");
      return;
    }

    try {
      // Add user message immediately with chat documents
      const userMessage = {
        message_id: `temp-user-${Date.now()}`,
        message_type: "user",
        message_text: message,
        timestamp: new Date().toISOString(),
        session_id: sessionId,
        // NEW: Include chat documents in user message display
        chat_documents: currentChatDocuments.map((doc) => doc.chat_document_id),
      };

      setMessages((prev) => [...prev, userMessage]);

      // Add streaming message placeholder
      const streamingMessageId = `streaming-${Date.now()}`;
      const streamingMessage = {
        message_id: streamingMessageId,
        message_type: "assistant",
        message_text: "",
        timestamp: new Date().toISOString(),
        session_id: sessionId,
        isStreaming: true,
      };

      setMessages((prev) => [...prev, streamingMessage]);

      // Clear any previous streaming content
      clearStreamingContent();

      console.log("Starting streaming message...");

      // Send streaming message with chat documents and selected model
      const completionData = await sendStreamingMessage(sessionId, message, {
        ...options,
        // NEW: Include chat documents in the AI request
        chatDocuments: currentChatDocuments.map((doc) => doc.chat_document_id),
        selectedModel: selectedModel,
      });

      console.log("Streaming completed, updating final message...");

      // Get the final content before clearing streaming
      const finalContent = getFinalStreamedContent() || streamingContent;

      // CRITICAL FIX: Update everything EXCEPT message_id to prevent React from creating new component
      setMessages((prev) =>
        prev.map((msg) =>
          msg.message_id === streamingMessageId
            ? {
                ...msg,
                // DON'T change message_id - keep the streaming ID to maintain component identity
                message_text: finalContent, // Set the final content immediately
                ai_provider: completionData.provider,
                model_used: completionData.model,
                processing_time_ms: completionData.processingTime,
                source_links: completionData.sources || [],
                isStreaming: false, // Mark as complete immediately
                server_message_id: completionData.messageId, // Store server ID separately if needed
              }
            : msg
        )
      );

      // Now complete the streaming state (this will clear streamingContent)
      completeStreaming();
      refreshSessions(); // Refresh sessions to update sidebar
    } catch (error) {
      console.error("Send message failed:", error);

      setMessages((prev) =>
        prev.filter(
          (msg) =>
            !msg.message_id.startsWith("temp-user-") &&
            !msg.message_id.startsWith("streaming-")
        )
      );

      clearStreamingContent();
    }
  };

  const handleNewGeneralChat = async () => {
    try {
      // Clear current session and messages immediately
      setCurrentGeneralSession(null);
      clearCurrentSession();

      // Reset the manual selection flag so auto-loading can work again
      setHasUserSelectedSession(false);

      const timestamp = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      const sessionName = `General Chat - ${timestamp}`;

      const newSession = await createSession(sessionName, null);

      if (newSession) {
        const sessionId = newSession.sessionId || newSession.session_id;

        if (sessionId) {
          const normalizedSession = {
            ...newSession,
            session_id: sessionId,
          };

          setCurrentGeneralSession(normalizedSession);
          await fetchSession(sessionId);
        }

        // Close sidebar on mobile
        setGeneralChatSidebarOpen(false);
      }
    } catch (error) {
      console.error("Failed to create new general chat session:", error);
    }
  };

  const handleGeneralSessionSelect = async (session) => {
    const sessionId = session.session_id || session.sessionId;

    // Mark that user has manually selected a session
    setHasUserSelectedSession(true);

    // Clear current session if switching to a different one
    if (
      currentGeneralSession?.session_id !== sessionId &&
      currentGeneralSession?.sessionId !== sessionId
    ) {
      setCurrentGeneralSession(null);
      clearCurrentSession();

      // Small delay to ensure state is cleared
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Normalize the session object
    const normalizedSession = {
      ...session,
      session_id: sessionId,
    };

    setCurrentGeneralSession(normalizedSession);
    await fetchSession(sessionId);
  };

  const handleCreateProposal = () => {
    // Check if user is admin
    if (user?.role !== "admin") {
      // Show a simple alert instead of opening the modal
      alert(
        "Only administrators can create proposals. Please contact your administrator for access."
      );
      return;
    }
    setShowCreateProposalModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateProposalModal(false);
  };

  const handleProposalCreated = (newProposal) => {
    setShowCreateProposalModal(false);
    fetchProposals();
    const proposalId = newProposal.proposalId || newProposal.proposal_id;
    navigate(`/workspace/${newProposal.proposalId}`);
  };

  // NEW: Handle predefined prompt selection
  const handlePredefinedPromptSelect = (prompt) => {
    // Dispatch custom event that MessageInput listens for
    window.dispatchEvent(
      new CustomEvent("predefinedPromptSelected", {
        detail: { prompt },
      })
    );
  };

  const activeProposals = proposals?.filter((p) => p.status === "Active") || [];
  const recentProposals = activeProposals.slice(0, 8);

  return (
    <Layout>
      <div className="space-y-4">
        {/* Welcome Header with Stats - COMPACT VERSION */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl p-3 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold">
                Welcome back, {user?.username}!
              </h1>
            </div>

            {/* All Stats in one line */}
            <div className="hidden md:flex items-center space-x-6">
              <div className="flex items-center space-x-1">
                <FontAwesomeIcon
                  icon="file"
                  className="text-primary-200 text-sm"
                />
                <p className="text-lg font-bold text-white">
                  {stats.totalProposals}
                </p>
                <p className="text-xs text-primary-100">Total</p>
              </div>

              <div className="flex items-center space-x-1">
                <FontAwesomeIcon
                  icon="clock"
                  className="text-primary-200 text-sm"
                />
                <p className="text-lg font-bold text-white">
                  {stats.activeProposals}
                </p>
                <p className="text-xs text-primary-100">Active</p>
              </div>

              <div className="flex items-center space-x-1">
                <FontAwesomeIcon
                  icon="comment"
                  className="text-primary-200 text-sm"
                />
                <p className="text-lg font-bold text-white">
                  {stats.chatSessions}
                </p>
                <p className="text-xs text-primary-100">Chats</p>
              </div>

              <div>
                <FontAwesomeIcon
                  icon="rocket"
                  className="text-2xl text-primary-200"
                />
              </div>
            </div>

            {/* Mobile: Just show rocket icon */}
            <div className="md:hidden">
              <FontAwesomeIcon
                icon="rocket"
                className="text-2xl text-primary-200"
              />
            </div>
          </div>
        </div>

        {/* Main Content - Updated Layout with Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* General AI Assistant with Sidebar - Takes up 3/5 of the width */}
          <div className="lg:col-span-4">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 h-screen max-h-[calc(100vh-170px)] flex">
              {/* General Chat Sidebar */}
              <GeneralChatSidebar
                isOpen={generalChatSidebarOpen}
                onClose={() => setGeneralChatSidebarOpen(false)}
                onNewChat={handleNewGeneralChat}
                currentSessionId={currentGeneralSession?.session_id}
                onSessionSelect={handleGeneralSessionSelect}
              />

              {/* Main Chat Area */}
              <div className="flex-1 flex flex-col min-w-0">
                {/* Chat Header */}
                {/* Chat Header - COMPACT VERSION */}
                <div className="p-3 border-b border-slate-200 bg-emerald-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() =>
                          setGeneralChatSidebarOpen(!generalChatSidebarOpen)
                        }
                        className="lg:hidden p-1.5 rounded-lg hover:bg-emerald-100 transition-colors"
                      >
                        <FontAwesomeIcon
                          icon="bars"
                          className="text-emerald-600 text-sm"
                        />
                      </button>
                      <div className="flex items-center space-x-2">
                        <FontAwesomeIcon
                          icon="robot"
                          className="text-emerald-600"
                        />
                        <h2 className="text-base font-semibold text-slate-900">
                          General AI Assistant
                        </h2>
                        <span className="text-sm text-slate-600 hidden sm:inline">
                          • Ask about proposals, best practices, or get help
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {/* AI Model Selector */}
                      <div className="relative">
                        <select
                          value={selectedModel}
                          onChange={(e) => setSelectedModel(e.target.value)}
                          disabled={modelsLoading || chatLoading || isTyping || isStreaming}
                          className="appearance-none bg-white border border-slate-300 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-slate-700 hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {modelsLoading ? (
                            <option value="">Loading models...</option>
                          ) : (
                            <>
                              <option value="">Select AI Model...</option>
                              <optgroup label="OpenAI">
                                {availableModels
                                  .filter((m) => m.provider === "openai")
                                  .map((model) => (
                                    <option key={model.model} value={model.model}>
                                      {model.name}
                                    </option>
                                  ))}
                              </optgroup>
                              <optgroup label="Anthropic">
                                {availableModels
                                  .filter((m) => m.provider === "anthropic")
                                  .map((model) => (
                                    <option key={model.model} value={model.model}>
                                      {model.name}
                                    </option>
                                  ))}
                              </optgroup>
                              <optgroup label="Google">
                                {availableModels
                                  .filter((m) => m.provider === "google")
                                  .map((model) => (
                                    <option key={model.model} value={model.model}>
                                      {model.name}
                                    </option>
                                  ))}
                              </optgroup>
                            </>
                          )}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                          <FontAwesomeIcon icon="chevron-down" className="text-xs" />
                        </div>
                      </div>

                      {/* Predefined Prompts Dropdown */}
                      <PredefinedPromptsDropdown
                        onSelectPrompt={handlePredefinedPromptSelect}
                        disabled={chatLoading || isTyping || isStreaming}
                      />
                    </div>
                  </div>
                </div>

                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {chatLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <LoadingSpinner size="lg" text="Loading chat..." />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center max-w-sm">
                        <FontAwesomeIcon
                          icon="robot"
                          className="text-4xl text-emerald-300 mb-4"
                        />
                        <h3 className="text-lg font-medium text-slate-900 mb-2">
                          How can I help you today?
                        </h3>
                        <p className="text-slate-600 mb-4 text-sm">
                          Ask me about proposal strategies, writing tips, or
                          general questions.
                        </p>
                        {/* NEW: Show upload hint when no documents are present */}
                        {/* <div className="space-y-2 text-sm text-slate-500">
                          <p>💡 Try asking:</p>
                          <ul className="space-y-1 text-xs">
                            <li>"What makes a winning proposal?"</li>
                            <li>"How do I analyze an RFP?"</li>
                            <li>"Show me proposal best practices"</li>
                          </ul>
                          <p className="mt-3 text-xs text-purple-600">
                            📎 You can also upload documents using the paperclip
                            icon below!
                          </p>
                        </div> */}
                      </div>
                    </div>
                  ) : (
                    <>
                      {messages.map((message, index) => {
                        // Handle streaming messages - pass the streamed content
                        if (message.isStreaming) {
                          return (
                            <MessageBubble
                              key={message.message_id || index}
                              message={{
                                ...message,
                                message_text:
                                  streamingContent || "AI is thinking...",
                              }}
                              isLatest={index === messages.length - 1}
                              isStreaming={true}
                              streamingContent={streamingContent}
                            />
                          );
                        }

                        // Regular messages - but for recently completed streaming, use the streamed content
                        const displayMessage = { ...message };
                        if (message.streamingComplete && streamingContent) {
                          displayMessage.message_text = streamingContent;
                        }

                        return (
                          <MessageBubble
                            key={message.message_id || index}
                            message={displayMessage}
                            isLatest={index === messages.length - 1}
                            isStreaming={false}
                          />
                        );
                      })}

                      {isTyping && (
                        <div className="flex items-center space-x-3 text-slate-500">
                          <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center">
                            <FontAwesomeIcon
                              icon="robot"
                              className="text-emerald-600 text-sm"
                            />
                          </div>
                          <div className="flex items-center space-x-1">
                            <div className="typing-indicator">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                            <span className="text-sm">AI is thinking...</span>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Chat Input */}
                <div className="border-t border-slate-200 p-4 bg-slate-50">
                  <MessageInput
                    onSendMessage={handleGeneralChatMessage}
                    disabled={chatLoading || isTyping || isStreaming}
                    placeholder="Ask me anything..."
                    maxLength={2000}
                    // NEW: Chat document props
                    sessionId={currentGeneralSession?.session_id}
                    chatDocuments={currentChatDocuments}
                    onUploadChatDocument={handleUploadChatDocument}
                    onRemoveChatDocument={handleRemoveChatDocument}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Active Proposals - Takes up 2/5 of the width */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 h-screen max-h-[calc(100vh-170px)] flex flex-col">
              {/* Header - Fixed at top */}
              <div className="p-4 border-b border-slate-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900 flex items-center">
                    <FontAwesomeIcon
                      icon="file"
                      className="mr-2 text-primary-500"
                    />
                    Active Proposals
                  </h2>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => navigate("/proposals")}
                      className="text-primary-500 hover:text-primary-600 transition-colors p-1"
                      title="View All"
                    >
                      <FontAwesomeIcon
                        icon="external-link-alt"
                        className="text-sm"
                      />
                    </button>
                    {user?.role === "admin" && (
                      <button
                        onClick={handleCreateProposal}
                        className="text-primary-500 hover:text-primary-600 transition-colors p-1"
                        title="New Proposal"
                      >
                        <FontAwesomeIcon icon="plus" className="text-sm" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Scrollable Content Area */}
              <div className="flex-1 overflow-y-auto p-6 pt-4">
                {proposalsError ? (
                  <ErrorMessage message={proposalsError} />
                ) : proposalsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner size="lg" text="Loading proposals..." />
                  </div>
                ) : recentProposals.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4">
                    {recentProposals.map((proposal) => (
                      <ProposalCard
                        key={proposal.proposal_id}
                        proposal={proposal}
                        compact={true}
                      />
                    ))}

                    {/* Show more proposals if there are many */}
                    {proposals && proposals.length > recentProposals.length && (
                      <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg text-center">
                        <p className="text-sm text-slate-600 mb-3">
                          Showing {recentProposals.length} of {proposals.length}{" "}
                          active proposals
                        </p>
                        <button
                          onClick={() => navigate("/proposals")}
                          className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                        >
                          View All Proposals →
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FontAwesomeIcon
                      icon="file"
                      className="text-4xl text-slate-300 mb-4"
                    />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">
                      No Active Proposals
                    </h3>
                    <p className="text-slate-600 mb-4">
                      Create your first proposal to get started with AI-powered
                      development.
                    </p>
                    <button
                      onClick={handleCreateProposal}
                      className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                    >
                      <FontAwesomeIcon icon="plus" className="mr-2" />
                      Create Your First Proposal
                    </button>
                  </div>
                )}
              </div>

              {/* Optional Footer - Fixed at bottom */}
              <div className="p-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
                <div className="text-xs text-slate-500 text-center">
                  {recentProposals.length} active proposal
                  {recentProposals.length !== 1 ? "s" : ""}
                  {proposals &&
                    proposals.length > recentProposals.length &&
                    ` • ${
                      proposals.length - recentProposals.length
                    } more available`}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Proposal Modal */}
      {user?.role === "admin" && (
        <CreateProposalModal
          isOpen={showCreateProposalModal}
          onClose={handleCloseModal}
          onProposalCreated={handleProposalCreated}
        />
      )}

      {/* Typing Animation Styles */}
      <style jsx>{`
        .typing-indicator {
          display: flex;
          space-x: 2px;
        }

        .typing-indicator span {
          height: 4px;
          width: 4px;
          background-color: #64748b;
          border-radius: 50%;
          display: inline-block;
          animation: typing 1.4s ease-in-out infinite both;
          margin-right: 2px;
        }

        .typing-indicator span:nth-child(1) {
          animation-delay: -0.32s;
        }

        .typing-indicator span:nth-child(2) {
          animation-delay: -0.16s;
        }

        @keyframes typing {
          0%,
          80%,
          100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </Layout>
  );
};

export default NewDashboard;
